import React from 'react';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import styles from './RangeSlider.module.scss';

interface RangeSliderProps {
  title: string;
  className?: string;
  absoluteMin: number;
  absoluteMax: number;
  minValue: number;
  maxValue: number;
  step?: number;
  id: string;
  unit?: string;
  minTooltipText?: (_value: string) => string;
  maxTooltipText?: (_value: string) => string;
  ariaLabelledby?: string;
  onChange: (_min: number, _max: number) => void;
}

const THUMB_SIZE_PX = 20;
const TOOLTIP_MARGIN_PCT = 10;

const RangeSlider = ({
  className,
  title,
  absoluteMin,
  absoluteMax,
  minValue,
  maxValue,
  step = 1,
  id,
  unit,
  minTooltipText = v => v,
  maxTooltipText = v => v,
  ariaLabelledby,
  onChange,
}: RangeSliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const trackWidth = trackRef.current?.clientWidth ?? 0;
  const [activeThumb, setActiveThumb] = useState<'min' | 'max' | null>(null);
  const [lastActiveThumb, setLastActiveThumb] = useState<'min' | 'max' | null>(
    activeThumb,
  );

  useEffect(() => {
    if (!activeThumb) return;
    setLastActiveThumb(activeThumb);
  }, [activeThumb]);

  const getSegments = (n: number): number => {
    if (n <= 10) return n;

    const step = Math.ceil(n / 10);
    const segments = Math.ceil(n / step);

    return segments;
  };

  // Convert pixel to value with step and limits
  const pixelToValue = (pixel: number) => {
    const clampedPx = Math.min(Math.max(pixel, 0), trackWidth - THUMB_SIZE_PX);
    const pct = clampedPx / (trackWidth - THUMB_SIZE_PX);
    const rawValue = absoluteMin + pct * (absoluteMax - absoluteMin);
    return Math.round(rawValue / step) * step;
  };

  // Decide tooltip position based on thumb position in px
  const getTooltipPosition = (thumbPct: number) => {
    if (thumbPct < TOOLTIP_MARGIN_PCT) return 'left';
    if (100 - thumbPct < TOOLTIP_MARGIN_PCT) return 'right';
    return 'center';
  };

  const onThumbPointerDown =
    (which: 'min' | 'max') => (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // Just in case, cut handlers that are registered in the native phase
      if (e.nativeEvent?.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
      (e.target as HTMLElement)?.setPointerCapture?.(e.pointerId);
      setActiveThumb(which);
    };

  // Drag handling con mouse
  useEffect(() => {
    const handlePointerMove = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!trackRef.current || !activeThumb) return;
      const rect = trackRef.current.getBoundingClientRect();
      const pixel = e.clientX - rect.left - THUMB_SIZE_PX / 2;
      const val = pixelToValue(pixel);

      if (activeThumb === 'min') {
        onChange(Math.min(val, maxValue), maxValue);
      } else {
        onChange(
          minValue,
          Math.max(val, minValue > 0 ? minValue : minValue + 1),
        );
      }
    };

    const handlePointerUp = () => setActiveThumb(null);

    if (activeThumb) {
      window.addEventListener('pointermove', handlePointerMove, {
        passive: false,
      });
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    }
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [
    activeThumb,
    minValue,
    maxValue,
    step,
    absoluteMin,
    absoluteMax,
    onChange,
  ]);

  const adjust = (thumb: 'min' | 'max', delta: number) => {
    if (thumb === 'min') {
      onChange(
        Math.max(Math.min(minValue + delta, maxValue - step), absoluteMin),
        maxValue,
      );
    } else {
      onChange(
        minValue,
        Math.min(Math.max(maxValue + delta, minValue + step), absoluteMax),
      );
    }
  };

  const handleKeyDown = (thumb: 'min' | 'max') => (e: KeyboardEvent) => {
    const actions: Record<string, () => void> = {
      ArrowUp: () => adjust(thumb, step),
      ArrowRight: () => adjust(thumb, step),
      ArrowDown: () => adjust(thumb, -step),
      ArrowLeft: () => adjust(thumb, -step),
      Home: () =>
        thumb === 'min'
          ? onChange(absoluteMin, maxValue)
          : onChange(minValue, minValue + step),
      End: () =>
        thumb === 'min'
          ? onChange(maxValue - step, maxValue)
          : onChange(minValue, absoluteMax),
    };
    if (actions[e.key]) {
      e.preventDefault();
      actions[e.key]();
    }
  };

  const formatValue = (value: number): string => {
    if (unit !== '%') return `${unit ?? ''}${value}`;
    else if (unit) return `${value}${unit}`;
    return value.toString();
  };

  const maxThumbPercent = (maxValue / absoluteMax) * 100;
  const minThumbPercent = (minValue / absoluteMax) * 100;
  const minTooltipPosition = getTooltipPosition(minThumbPercent);
  const maxTooltipPosition = getTooltipPosition(maxThumbPercent);

  const totalTicks = getSegments(absoluteMax);
  const tickArray = Array.from({ length: totalTicks });

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.header}>
        <label className={styles.title} id={`${id}-label`}>
          {title}
        </label>
      </div>

      <div aria-hidden="true" className={styles.labels}>
        <span>{formatValue(absoluteMin)}</span>
        <span>{formatValue(absoluteMax)}</span>
      </div>

      <div
        aria-labelledby={`${id}-label${
          ariaLabelledby ? ` ${ariaLabelledby}` : ''
        }`}
        className={styles.sliderContainer}
        ref={trackRef}
        role="group"
      >
        {/* Track */}
        <div className={styles.track}>
          {tickArray?.map((_, i) => (
            <span
              className={styles.tick}
              key={i}
              style={{ left: `${(i / totalTicks) * 100}%` }}
            />
          ))}

          {/* Range Highlight */}
          <div
            className={styles.range}
            style={{
              left: `${minThumbPercent}%`,
              right: `clamp(0px, calc(100% - ${maxThumbPercent}% - ${THUMB_SIZE_PX}px), 100%)`,
            }}
          />

          {/* Thumb Min */}
          <div
            aria-valuemax={maxValue - step}
            aria-valuemin={absoluteMin}
            aria-valuenow={minValue}
            aria-valuetext={minTooltipText(formatValue(minValue))}
            className={classNames(styles.thumb, {
              [styles.thumb__active]: lastActiveThumb === 'min',
            })}
            onBlur={() => {
              setActiveThumb(null);
            }}
            onFocus={() => {
              setActiveThumb('min');
            }}
            onKeyDown={handleKeyDown('min')}
            onPointerDown={onThumbPointerDown('min')}
            role="slider"
            style={{
              left: `clamp(0px, ${Math.min(
                minThumbPercent,
                maxThumbPercent,
              )}%, calc(100% - ${THUMB_SIZE_PX}px))`,
            }}
            tabIndex={0}
          >
            <div
              className={classNames(
                styles.tooltip,
                styles[`tooltip_${minTooltipPosition}`],
              )}
            >
              <div className={styles.peak} />
              {minTooltipText(formatValue(minValue))}
            </div>
          </div>

          {/* Thumb Max */}
          <div
            aria-valuemax={absoluteMax}
            aria-valuemin={minValue + step}
            aria-valuenow={maxValue}
            aria-valuetext={maxTooltipText(formatValue(maxValue))}
            className={classNames(styles.thumb, {
              [styles.thumb__active]: lastActiveThumb === 'max',
            })}
            onBlur={() => {
              setActiveThumb(null);
            }}
            onFocus={() => {
              setActiveThumb('max');
            }}
            onKeyDown={handleKeyDown('max')}
            onPointerDown={onThumbPointerDown('max')}
            role="slider"
            style={{
              left: `clamp(calc(${Math.min(
                maxThumbPercent,
                minThumbPercent,
              )}% - 20px), ${maxThumbPercent}%, calc(100% - ${THUMB_SIZE_PX}px))`,
            }}
            tabIndex={0}
          >
            <div
              className={classNames(
                styles.tooltip,
                styles[`tooltip_${maxTooltipPosition}`],
              )}
            >
              <div className={styles.peak} />
              {maxTooltipText(formatValue(maxValue))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
